import { css } from '@emotion/react';
import { Chip, Skeleton, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { getRoundColor } from '../../../components/AgTable/cell-renderers/RoundRenderer';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { useRoundDocuments } from '../../../services/queries-hooks/MaggieRoundDocumentQueriesHooks';
import { roundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { colors } from '../../../theme/colors';
import { transparentBgStyle } from '../../../theme/component-styles';
import { FMT } from '../../../util/formatter-service';
import {
  calculateRoundStatus,
  DealDocStatusBadge,
} from '../../CompanyProfiles/components/ActivityDrawer/DealDocs/DealDocStatusBadge';
import { roundChipCss } from '../../CompanyProfiles/components/ActivityDrawer/DealDocs/roundChip.styles';
import {
  roundCardHoverCss,
  roundCardSelectedCss,
} from '../../CompanyProfiles/components/ActivityDrawer/activity-drawer-common.styles';

// Self-contained rather than layered over the shared `expandableCardCss`, so this
// card can diverge without touching the Activity-drawer side panel that reuses it.
const cardButtonCss = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 0.75rem;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  background: ${transparentBgStyle.background};
  border: 1px solid ${colors.neutral[80]};
  border-radius: 4px;
  /* Air kept around the card when it is scrolled into view, so it never sits flush with the list edge. */
  scroll-margin: 12px 0;

  &:focus-visible {
    outline: 2px solid ${colors.primary[50]};
    outline-offset: 1px;
  }
`;

// Matches the side-panel RoundCard title (1rem / 1.5).
const titleCss = css`
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const dateCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

const chipRowCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const statusErrorCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.critical[50]};
  white-space: nowrap;
`;

interface DealRoundCardProps {
  round: IFinanceRoundDataModel;
  isSelected: boolean;
  onSelect: (roundId: number) => void;
}

/**
 * A single selectable financing-round card in the Deals-tab left column.
 * Shows the round name, its closing date (for quick comparison between rounds),
 * its colored round chip, and the doc-completeness status. Fetches the round's
 * documents for status.
 */
export const DealRoundCard: FC<DealRoundCardProps> = ({ round, isSelected, onSelect }) => {
  const roundsByIdMap = useAtomValue(roundsByIdMapAtom);
  const { data: documents, isLoading, isError } = useRoundDocuments(round.id);

  const investmentRound = round.roundId ? roundsByIdMap.get(round.roundId) : null;
  const status = calculateRoundStatus(documents ?? []);

  return (
    <DealRoundCardView
      round={round}
      isSelected={isSelected}
      onSelect={onSelect}
      stage={
        investmentRound?.displayName ? { id: investmentRound.id, label: investmentRound.displayName } : null
      }
      status={
        isLoading ? (
          <Skeleton variant='text' width={110} height={20} />
        ) : isError ? (
          <span css={statusErrorCss}>Unable to load docs</span>
        ) : (
          <DealDocStatusBadge status={status} />
        )
      }
    />
  );
};

export interface DealRoundCardViewProps extends DealRoundCardProps {
  /** Investment-round stage for the coloured chip; null renders no chip. */
  stage: { id: number | undefined; label: string } | null;
  /** Doc-completeness slot: badge, skeleton or error. */
  status: ReactNode;
}

/** Rendering half of the card, so a preview can show sample rounds without the document query. */
export const DealRoundCardView: FC<DealRoundCardViewProps> = ({
  round,
  isSelected,
  onSelect,
  stage,
  status,
}) => {
  const chipColors = getRoundColor(stage?.id);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // A round picked in the Activity drawer has to land in view in this list too.
  useEffect(() => {
    if (isSelected) {
      buttonRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isSelected]);

  return (
    <button
      ref={buttonRef}
      type='button'
      aria-pressed={isSelected}
      css={[cardButtonCss, roundCardHoverCss, isSelected && roundCardSelectedCss]}
      onClick={() => onSelect(round.id)}
    >
      <Typography css={titleCss}>{round.name || 'Unknown Round'}</Typography>

      {round.roundDate && <span css={dateCss}>{FMT.format('date', round.roundDate)}</span>}

      <div css={chipRowCss}>
        {stage && (
          <Chip
            label={stage.label}
            size='small'
            css={roundChipCss(chipColors.background, chipColors.color)}
          />
        )}
        {status}
      </div>
    </button>
  );
};
